import {
  OAuthProvider,
  type User,
  getRedirectResult,
  signInWithRedirect,
} from "firebase/auth"
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import {
  useAuthState,
  useSignInWithGoogle,
  useSignOut,
} from "react-firebase-hooks/auth"

import { auth } from "../firebase"
import type { CustomClaims } from "../types/auth"

interface AuthContextType {
  user: User | undefined
  loading: boolean
  claims: CustomClaims | undefined
  error: Error | undefined
  signInWithGoogle: () => Promise<void>
  signInWithMicrosoft: () => Promise<void>
  signOut: () => Promise<boolean>
}

const AuthContext = createContext<AuthContextType>({
  user: undefined,
  loading: false,
  error: undefined,
  claims: undefined,
  signInWithGoogle: async () => {},
  signInWithMicrosoft: async () => {},
  signOut: () => Promise.resolve(false),
})

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, userLoading, userError] = useAuthState(auth)
  const [signOut, signOutLoading, signOutError] = useSignOut(auth)
  const [claimsLoading, setClaimsLoading] = useState<boolean>(true)
  const [claimsError, setClaimsError] = useState<Error | undefined>()
  const [claims, setClaims] = useState<CustomClaims | undefined>()
  const [_signInWithGoogle, , googleLoading, googleError] =
    useSignInWithGoogle(auth)
  const [microsoftLoading, setMicrosoftLoading] = useState(true)
  const [microsoftError, setMicrosoftError] = useState<Error | undefined>()

  // Handle the redirect result when returning from Microsoft login.
  useEffect(() => {
    getRedirectResult(auth)
      .catch((err) => setMicrosoftError(err as Error))
      .finally(() => setMicrosoftLoading(false))
  }, [])

  const _refreshClaims = useCallback(async (user: User | undefined | null) => {
    if (!user) {
      setClaims(undefined)
      setClaimsLoading(false)
      return
    }
    setClaimsLoading(true)
    try {
      const tokenResult = await user.getIdTokenResult()
      setClaims(tokenResult.claims as unknown as CustomClaims)
    } catch (err) {
      setClaimsError(Error("Error fetching claims"))
    } finally {
      setClaimsLoading(false)
    }
  }, [])

  useEffect(() => {
    void _refreshClaims(user)
  }, [_refreshClaims, user])

  const signInWithGoogle = useCallback(async () => {
    await _signInWithGoogle(
      [
        "https://www.googleapis.com/auth/userinfo.email",
        "https://www.googleapis.com/auth/userinfo.profile",
      ],
      {
        prompt: "select_account",
      },
    )
  }, [_signInWithGoogle])

  const signInWithMicrosoft = useCallback(async () => {
    const provider = new OAuthProvider("microsoft.com")
    provider.addScope("email")
    provider.addScope("profile")
    provider.setCustomParameters({ prompt: "select_account" })
    await signInWithRedirect(auth, provider)
  }, [])

  const value: AuthContextType = {
    user: user ?? undefined,
    loading:
      userLoading ||
      claimsLoading ||
      signOutLoading ||
      googleLoading ||
      microsoftLoading,
    error:
      userError || signOutError || claimsError || googleError || microsoftError,
    claims: claims,
    signOut,
    signInWithGoogle,
    signInWithMicrosoft,
  }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = (): AuthContextType => {
  return useContext(AuthContext)
}
